import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CALENDLY_URL = 'https://calendly.com/mandieozee/15min';

interface SessionType {
  id: string;
  title: string;
  subtitle: string;
  duration: string;
  description: string;
  icon: string;
  tags: string[];
  timeSlots: string[];
  highlighted?: boolean;
  href?: string;
}

const sessionTypes: SessionType[] = [
  {
    id: 'clarity-call',
    title: 'Clarity Call',
    subtitle: 'Your first exhale',
    duration: '30 min',
    description: 'A no-pressure conversation to explore where you are, what you are carrying, and whether working together feels aligned. Think of it as two women being honest in a room, no sales pitch, no performance.',
    icon: 'ri-psychotherapy-line',
    tags: ['Intro Call'],
    timeSlots: ['Mon — 10:00 AM', 'Tue — 2:00 PM', 'Wed — 11:00 AM', 'Thu — 3:00 PM', 'Fri — 9:00 AM'],
    highlighted: false,
    href: 'https://paystack.com/buy/private-clarity-call-sdonrr',
  },
  {
    id: 'one-on-one-coaching',
    title: '1:1 Coaching / Therapy',
    subtitle: 'A sacred space for your unfolding',
    duration: '60 min',
    description: 'A personalized therapeutic container where we meet exactly where you are, in the grief, the transition, the quiet knowing. One-on-one work that honors your nervous system, your story, and your pace. No scripts, just presence.',
    icon: 'ri-user-heart-line',
    tags: ['1-on-1', 'Personalized', 'Deep Work'],
    timeSlots: ['Mon — 1:00 PM', 'Wed — 9:00 AM', 'Thu — 1:00 PM', 'Fri — 11:00 AM'],
    highlighted: true,
  },
  {
    id: 'group-coaching',
    title: 'Group Coaching Session',
    subtitle: 'Healing in sisterhood',
    duration: '90 min',
    description: 'A facilitated group space for women walking parallel paths. You are not broken, and you are not alone. This is where the performance ends and the real conversation begins, held in sacred community, witnessed in collective courage.',
    icon: 'ri-team-line',
    tags: ['Group', 'Community', 'Monthly'],
    timeSlots: ['First Saturday — 10:00 AM', 'Third Thursday — 6:00 PM'],
    highlighted: false,
  },
  {
    id: 'home-protocol',
    title: 'HOME',
    subtitle: 'Honoring Our Mind & Emotions',
    duration: '12 weeks',
    description: 'A structured integrative protocol designed to bring you back home to yourself. Rooted in somatic awareness, emotional regulation, and nervous system healing, this is for the woman ready to do the deeper work, week by week, layer by layer, with Mandie as your steady guide.',
    icon: 'ri-home-heart-line',
    tags: ['Structured', '12-Week', 'Somatic'],
    timeSlots: ['Cohorts begin monthly', 'Individual pacing available'],
    highlighted: false,
  },
];

const weekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const availabilityGrid: Record<string, ('morning' | 'afternoon' | 'evening' | null)[]> = {
  'Clarity Call': ['morning', 'afternoon', 'morning', 'afternoon', 'morning', null],
  '1:1 Coaching': ['afternoon', null, 'morning', 'afternoon', 'morning', null],
  'Group Coaching': [null, null, null, 'evening', null, 'morning'],
  'HOME': [null, 'morning', null, 'morning', null, null],
};

export default function SessionTypesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'sessions' | 'schedule'>('sessions');

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        gridRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="session-types"
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        {/* Section heading */}
        <div className="text-center mb-12 md:mb-16">
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-4">
            Sessions & Availability
          </p>
          <h2 className="font-heading text-xl md:text-3xl lg:text-5xl text-[#1A1A1A] mb-6 leading-tight">
            Find your rhythm.
            <br />
            Choose your depth.
          </h2>
          <p className="font-body text-sm md:text-base text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto">
            Each session type is designed for a different season of the journey. Read through, feel into what resonates, and book directly into Mandie's calendar.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-[#F0EDEA] rounded-full p-1">
            <button
              onClick={() => setActiveTab('sessions')}
              className={`px-6 py-2.5 rounded-full text-sm font-body font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === 'sessions'
                  ? 'bg-white text-[#1A1A1A] shadow-sm'
                  : 'text-[#8A8A8A] hover:text-[#4A4A4A]'
              }`}
            >
              Session Types
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-6 py-2.5 rounded-full text-sm font-body font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === 'schedule'
                  ? 'bg-white text-[#1A1A1A] shadow-sm'
                  : 'text-[#8A8A8A] hover:text-[#4A4A4A]'
              }`}
            >
              Weekly Schedule
            </button>
          </div>
        </div>

        {/* Session Types Grid */}
        {activeTab === 'sessions' && (
          <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sessionTypes.map((session) => (
              <div
                key={session.id}
                className={`relative rounded-lg border p-6 md:p-8 flex flex-col transition-all duration-300 ${
                  session.highlighted
                    ? 'border-[#C9A84C]/40 bg-[#FDF9F5]'
                    : 'border-[#E0DBD5] bg-white hover:border-[#C9A84C]/30'
                }`}
              >
                {session.highlighted && (
                  <span className="absolute -top-3 left-6 px-4 py-1 bg-[#C9A84C] text-white text-xs font-body font-medium rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                )}

                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C9A84C]/10 text-[#C9A84C] flex-shrink-0">
                    <i className={`${session.icon} text-xl`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg md:text-xl text-[#1A1A1A] mb-1">
                      {session.title}
                    </h3>
                    <p className="font-quote text-sm italic text-[#8A8A8A]">
                      {session.subtitle}
                    </p>
                  </div>
                </div>

                {/* Duration & Price */}
                <div className="flex items-center gap-4 mb-4">
                  <span className="flex items-center gap-1.5 text-sm font-body text-[#4A4A4A]">
                    <i className="ri-time-line text-[#C9A84C]" />
                    {session.duration}
                  </span>
                </div>

                {/* Description */}
                <p className="font-body text-sm text-[#4A4A4A] leading-relaxed mb-5 flex-1">
                  {session.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {session.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs font-body rounded-full bg-[#F0EDEA] text-[#6A6A6A] whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Available Time Slots Preview */}
                <div className="mb-5">
                  <p className="text-xs font-body text-[#8A8A8A] uppercase tracking-wider mb-2">
                    Typical availability
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {session.timeSlots.map((slot) => (
                      <span
                        key={slot}
                        className="px-3 py-1.5 text-xs font-body rounded-full border border-[#C9A84C]/20 bg-[#C9A84C]/5 text-[#6A6A6A] whitespace-nowrap"
                      >
                        {slot}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <a
                  href={session.href || CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-body font-medium transition-all duration-300 whitespace-nowrap ${
                    session.highlighted
                      ? 'bg-[#D4006A] text-white hover:bg-[#B3005A]'
                      : 'bg-[#1A1A1A] text-white hover:bg-[#333333]'
                  }`}
                >
                  <i className="ri-calendar-check-line" />
                  Book This Session
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Weekly Schedule View */}
        {activeTab === 'schedule' && (
          <div ref={gridRef} className="bg-white border border-[#E0DBD5] rounded-lg p-6 md:p-10">
            <p className="font-body text-xs text-[#8A8A8A] uppercase tracking-wider mb-8 text-center">
              Weekly Availability Overview
            </p>

            {/* Schedule Grid */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr>
                    <th className="text-left pb-4 font-body text-xs text-[#8A8A8A] uppercase tracking-wider">
                      Session
                    </th>
                    {weekDays.map((day) => (
                      <th
                        key={day}
                        className="text-center pb-4 font-body text-xs text-[#8A8A8A] uppercase tracking-wider"
                      >
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(availabilityGrid).map(([session, slots], idx) => (
                    <tr key={session} className={idx % 2 === 0 ? 'bg-[#FDFAF8]' : 'bg-white'}>
                      <td className="py-4 pr-4 font-body text-sm text-[#1A1A1A] font-medium whitespace-nowrap">
                        {session}
                      </td>
                      {slots.map((slot, i) => (
                        <td key={i} className="py-4 px-2 text-center">
                          {slot ? (
                            <span
                              className={`inline-block px-3 py-1 text-xs font-body rounded-full ${
                                slot === 'morning'
                                  ? 'bg-[#E8F5E9] text-[#2E7D32]'
                                  : slot === 'afternoon'
                                  ? 'bg-[#FDF9F5] text-[#C9A84C]'
                                  : 'bg-[#F3E5F5] text-[#7B1FA2]'
                              }`}
                            >
                              {slot === 'morning' ? 'Morning' : slot === 'afternoon' ? 'Afternoon' : 'Evening'}
                            </span>
                          ) : (
                            <span className="inline-block w-3 h-3 rounded-full bg-[#E0DBD5]/50" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#E8F5E9]" />
                <span className="text-xs font-body text-[#6A6A6A]">Morning (8AM — 12PM)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#FDF9F5]" />
                <span className="text-xs font-body text-[#6A6A6A]">Afternoon (12PM — 4PM)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#F3E5F5]" />
                <span className="text-xs font-body text-[#6A6A6A]">Evening (4PM — 7PM)</span>
              </div>
            </div>

            {/* CTA under schedule */}
            <div className="text-center mt-10">
              <p className="font-body text-sm text-[#4A4A4A] mb-4">
                Ready to claim your time?
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-calendar-check-line" />
                Open Full Calendar
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}